/**
 * @file ConsentRevisionSheet.tsx
 * @description Шторка «Смотреть» редакции текстов согласия (phase19.16,
 * Р3г; uiux-r3g-consent-texts §2): все поля только для чтения, тот же
 * предпросмотр и [Взять за основу] — «вернуть» редакцию нельзя, только
 * выпустить N+1 с её текстом (SB135). Справа, на 360 — во весь экран.
 * Загруженная редакция хранится с ключом «вид:номер» (ревью М1): при смене
 * строки до ответа — скелет, а не тексты прежней редакции.
 */

import type { ConsentTextKind } from "@chaiberi/shared";
import { useEffect, useState } from "react";

import { consentTextsApi, type ConsentTextRevision } from "../../../lib/api-consent-texts";
import { CONSENT_TEXT_TABS } from "../../../lib/platform/consent-texts-fields";
import { revisionLine } from "../../../lib/platform/consent-texts-format";
import { ListError } from "../../ui/ListStates";
import { Button } from "../../ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../../ui/sheet";
import { Skeleton } from "../../ui/skeleton";
import { ConsentTextsFieldList } from "./ConsentTextsFieldList";
import { ConsentTextsPreview } from "./ConsentTextsPreview";

interface Props {
  kind: ConsentTextKind;
  /** Номер открытой редакции; null — шторка закрыта. */
  number: number | null;
  onClose: () => void;
  /** «Взять за основу»: страница спросит про черновик и откроет форму. */
  onTakeBase: (number: number) => void;
}

type LoadState = { status: "loading" } | { status: "error" } | { status: "ready"; revision: ConsentTextRevision };

export function ConsentRevisionSheet({ kind, number, onClose, onTakeBase }: Props) {
  const [nonce, setNonce] = useState(0);
  const key = `${kind}:${number ?? ""}:${nonce}`;
  const [loaded, setLoaded] = useState<{ key: string; value: LoadState } | null>(null);

  useEffect(() => {
    if (number === null) return;
    let alive = true;
    consentTextsApi
      .get(kind, number)
      .then((revision): LoadState => ({ status: "ready", revision }))
      .catch((): LoadState => ({ status: "error" }))
      .then((value) => {
        if (alive) setLoaded({ key, value });
      });
    return () => {
      alive = false;
    };
  }, [kind, number, key]);

  const state: LoadState = loaded?.key === key ? loaded.value : { status: "loading" };

  const revision = state.status === "ready" ? state.revision : null;
  return (
    <Sheet open={number !== null} onOpenChange={(open) => (!open ? onClose() : undefined)}>
      <SheetContent side="right" className="w-full max-w-none overflow-y-auto sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle>Редакция {number}</SheetTitle>
          <SheetDescription>{revision ? revisionLine(revision) : "Только для чтения"}</SheetDescription>
        </SheetHeader>
        {state.status === "loading" ? <Skeleton className="h-64 w-full" /> : null}
        {state.status === "error" ? (
          <ListError message="Не удалось загрузить редакцию." onRetry={() => setNonce((n) => n + 1)} />
        ) : null}
        {revision ? (
          <div className="flex flex-col gap-6">
            <p className="type-body m-0 break-words text-on-surface">«{revision.note}»</p>
            <Button
              variant="brand"
              className="min-h-12 self-start"
              onClick={() => onTakeBase(revision.number)}
            >
              Взять за основу
            </Button>
            <ConsentTextsPreview kind={kind} texts={revision.texts} />
            {CONSENT_TEXT_TABS.filter((t) => t.kind === kind).map((t) => (
              <section key={t.value} className="flex flex-col gap-3">
                <h3 className="type-card-title m-0 text-on-surface">{t.label}</h3>
                <ConsentTextsFieldList tab={t.value} kind={kind} texts={revision.texts} readOnly idPrefix="sheet-" />
              </section>
            ))}
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
